<script setup lang="ts">
import { Ref, computed, nextTick, ref } from 'vue';
import BaseValue from '@/components/BaseValue.vue';
import {
  walletNftsPlatformId,
  walletTokensPlatformId,
  nativeStakePlatformId,
  API_URL,
} from '@/constants';
import { useFetch } from '@vueuse/core';
import {
  LlamaProtocol,
  NetworkIdType,
  Platform,
  networks,
  networksAsArray,
  publicBearerToken,
} from '@sonarwatch/portfolio-core';
import BaseNetworkIcon from '@/components/BaseNetworkIcon.vue';
import usePlatforms from '@/helpers/usePlatforms';

type EPlatform = Platform & {
  protocol?: LlamaProtocol;
};
const itemsNetwork = networksAsArray.toSorted((a, b) =>
  a.name.localeCompare(b.name),
);
const ignoredPlatformIds = [
  walletNftsPlatformId,
  walletTokensPlatformId,
  nativeStakePlatformId,
];

const affiliateDialog = ref(false);
const searchInput: Ref<null | string> = ref(null);
const selectedNetwork: Ref<string[]> = ref([]);
const selectedCategory: Ref<string[]> = ref([]);

const { data: platformsData } = usePlatforms();

const platforms = computed(() => {
  if (!llamaProtocols.value) return [];
  return (platformsData?.value || []).reduce((platforms: EPlatform[], p) => {
    if (ignoredPlatformIds.includes(p.id)) return platforms;
    platforms.push({
      ...p,
      protocol: llamaProtocols.value![p.id] || undefined,
    });
    return platforms;
  }, []);
});

const itemsCategories = computed(() => {
  return [
    ...new Set(platforms.value.map((p) => p.protocol?.categories || []).flat()),
  ].sort();
});

const fPlatforms = computed(() => {
  let fPlatforms = [...platforms.value];

  if (searchInput.value !== null) {
    const searchStr = searchInput.value.toLowerCase();
    fPlatforms = fPlatforms.filter((p) => {
      return p.name.toLocaleLowerCase().includes(searchStr);
    });
  }

  if (selectedNetwork.value.length !== 0) {
    fPlatforms = fPlatforms.filter((p) => {
      return (
        p.protocol &&
        selectedNetwork.value.every((v) =>
          p.protocol!.networkIds.includes(v as NetworkIdType),
        )
      );
    });
  }

  if (selectedCategory.value.length !== 0) {
    fPlatforms = fPlatforms.filter((p) => {
      return (
        p.protocol &&
        selectedCategory.value.every((v) => p.protocol!.categories.includes(v))
      );
    });
  }

  return fPlatforms;
});

async function blur() {
  await nextTick();
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
}

const { data: llamaProtocols, isFetching } = useFetch(
  new URL('portfolio/cache/llama-protocols/llama-protocols', API_URL).href,
  { headers: { Authorization: `Bearer ${publicBearerToken}` } },
  {
    updateDataOnError: true,
    onFetchError(ctx) {
      if (ctx.data === null) ctx.data = {} as Record<string, LlamaProtocol>;
      return ctx;
    },
    timeout: 10000,
  },
)
  .get()
  .json<Record<string, LlamaProtocol>>();
</script>

<template>
  <!-- eslint-disable vue/valid-v-slot -->

  <!-- Affiliate disclosures dialog -->
  <v-dialog v-model="affiliateDialog" width="auto">
    <v-card max-width="500" title="Support us">
      <v-card-text>
        <div>
          Links on this page may contain affiliate links.<br />
          Jupiter may be compensated when you sign up and use these affiliate
          platforms.
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="affiliateDialog = false">Ok</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Main container -->
  <v-container style="max-width: 900px">
    <div class="d-flex align-baseline mb-1">
      <h2 class="text-2xl font-bold">Platforms</h2>

      <v-spacer />
      <v-btn size="small" variant="plain" @click="affiliateDialog = true">
        Affiliate disclosures
      </v-btn>
    </div>
    <p class="text-subtitle-2 mb-6 text-medium-emphasis">
      A list of all supported platforms on SonarWatch ordered by TVL.
    </p>
    <div class="d-flex flex-wrap">
      <v-text-field
        v-model="searchInput"
        clearable
        class="mb-4"
        style="width: 90px"
        hide-details
        variant="solo-filled"
        density="compact"
        label="Search platform"
        single-line
        flat
        v-on:update:modelValue="
          (e) => {
            if (e === null) blur();
          }
        "
      ></v-text-field>
      <v-spacer></v-spacer>
      <!-- @disabled-jup -->
      <!-- <v-select
        class="mr-2 mb-4"
        style="max-width: 180px; min-width: 140px"
        density="compact"
        hide-details
        label="Chains"
        v-model="selectedNetwork"
        variant="solo-filled"
        flat
        :items="itemsNetwork"
        item-value="id"
        item-title="name"
        clearable
        multiple
        v-on:update:modelValue="
          (e) => {
            if (e.length === 0) blur();
          }
        "
        :transition="false"
      >
        <template #selection="{ index }">
          <span class="text-caption" v-if="index === 0">
            {{ selectedNetwork.length }} selected
          </span>
        </template>
      </v-select> -->
      <v-select
        class="mb-4"
        style="max-width: 240px; min-width: 180px"
        density="compact"
        hide-details
        label="Categories"
        v-model="selectedCategory"
        variant="solo-filled"
        flat
        :items="itemsCategories"
        item-value="id"
        item-title="name"
        clearable
        multiple
        v-on:update:modelValue="
          (e) => {
            if (e.length === 0) blur();
          }
        "
        :transition="false"
      >
        <template #selection="{ index }">
          <span class="text-caption" v-if="index === 0">
            {{ selectedCategory.length }} selected
          </span>
        </template>
      </v-select>
    </div>

    <div class="text-caption text-medium-emphasis text-left">
      {{
        isFetching
          ? '&#8203;'
          : `${fPlatforms.length} platform${fPlatforms.length > 1 ? 's' : ''}`
      }}
    </div>
    <v-data-table
      class="rounded"
      :items="fPlatforms"
      :loading="isFetching"
      :show-current-page="false"
      :items-per-page="-1"
      :must-sort="true"
      :hover="true"
      :sort-by="[{ key: 'tvl', order: 'desc' }]"
      :headers="[
        { title: 'Name', key: 'name', align: 'start', sortable: true },
        {
          title: 'Categories',
          key: 'protocol.categories',
          align: 'start',
          sortable: false,
        },
        {
          title: 'TVL',
          key: 'tvl',
          align: 'end',
          sortable: true,
        },
        { title: 'Links', key: 'links', align: 'end', sortable: false },
      ]"
    >
      <template v-slot:item.name="{ item }">
        <div class="d-flex align-center">
          <v-avatar :image="item.image" size="x-small" class="mr-2"></v-avatar>
          <div>
            {{ item.name }}
          </div>
          <v-tooltip
            location="top"
            v-if="item.protocol && item.protocol.networkIds.length > 0"
            transition="no"
          >
            <template v-slot:activator="{ props }">
              <span
                v-bind="props"
                size="x-small"
                class="text-medium-emphasis text-caption ml-2 text-no-wrap"
              >
                {{ item.protocol.networkIds.length }} chain{{
                  item.protocol.networkIds.length > 1 ? 's' : ''
                }}
              </span>
            </template>
            <div>
              <span
                v-for="networkId in item.protocol.networkIds"
                :key="networkId"
              >
                <BaseNetworkIcon
                  :network-id="networkId"
                  size="18"
                  class="mr-1"
                  colored
                ></BaseNetworkIcon>
                {{ networks[networkId].name }}<br />
              </span>
            </div>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:item.tvl="{ item }">
        <BaseValue
          :value="item.protocol?.tvl || undefined"
          :config="{ compact: true }"
        />
      </template>
      <template v-slot:item.protocol.categories="{ item }">
        <div v-if="item.protocol" class="pt-1">
          <v-chip
            class="mr-1 mb-1"
            size="small"
            elevation="0"
            v-for="category in item.protocol.categories"
            :key="category"
            :value="category"
            >{{ category }}</v-chip
          >
        </div>
      </template>
      <template v-slot:item.links="{ item }">
        <div class="d-flex justify-end">
          <v-btn
            v-if="item.referral || item.website || item.protocol?.url"
            icon="fa:fas fa-globe"
            size="x-small"
            variant="plain"
            :title="item.referral || item.website || item.protocol?.url"
            :href="item.referral || item.website || item.protocol?.url"
            target="_blank"
            rel="noopener"
          />
          <v-btn
            v-if="item.twitter || item.protocol?.twitter"
            icon="fa:fab fa-x-twitter"
            size="x-small"
            variant="plain"
            title="Twitter"
            :href="item.twitter || item.protocol?.twitter || undefined"
            target="_blank"
            rel="noopener"
          />
        </div>
      </template>
      <template #bottom> </template>
    </v-data-table>

    <div class="my-2 text-caption">
      <span>One missing? </span>
      <a
        href="https://feedback.sonar.watch/b/Features"
        target="_blank"
        rel="noopener"
        class="text-primary"
        >Create a request</a
      >
      or
      <a
        href="https://docs.sonar.watch/open-source/list-your-project/where-to-start"
        target="_blank"
        rel="noopener"
        class="text-primary"
        >contribute</a
      >.
    </div>
  </v-container>
</template>
